import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AGENT_REQUEST_TYPE_LABELS, type AgentRequestType } from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { PageHeader } from "./ui/PageHeader";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface QueueRow {
  id: string;
  type: AgentRequestType;
  status: string;
  createdAt: string;
  notes: string | null;
  formData: Record<string, unknown>;
  agentUser: { id: string; email: string };
}

export function AgentRequestQueue() {
  const token = getSessionToken();
  const [requests, setRequests] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadQueue() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/agent-requests/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load queue");
      setRequests(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }

  async function resolveRequest(id: string, status: "APPROVED" | "REJECTED") {
    if (!token) return;
    setResolvingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/agent-requests/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status, notes: resolveNotes[id]?.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update request");
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update request");
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <section className="space-y-5">
      <PageHeader
        title="Agent request queue"
        description="Review pending hierarchy, termination, commission, Intelelead invite, and Insurance Toolkit add-agent requests."
        actions={<Button size="sm" variant="secondary" onClick={() => void loadQueue()}>Refresh</Button>}
      />

      {loading && (
        <div role="status" aria-busy="true" className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-6 text-sm text-content-muted">
          Loading request queue…
        </div>
      )}
      {error && <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">{error}</div>}

      {!loading && requests.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised p-10 text-center">
          <p className="font-medium text-content-primary">Queue clear</p>
          <p className="mt-1 text-sm text-content-muted">No pending agent requests.</p>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        {requests.map((request) => (
          <article key={request.id} className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-content-primary">{AGENT_REQUEST_TYPE_LABELS[request.type]}</p>
                <p className="mt-1 text-xs text-content-secondary">
                  Agent:{" "}
                  <Link to={`/dashboard/agents/${request.agentUser.id}`} className="text-accent hover:opacity-80">
                    {request.agentUser.email}
                  </Link>
                </p>
                <p className="text-xs text-content-muted">{new Date(request.createdAt).toLocaleString()}</p>
              </div>
              <span className="rounded-md border border-status-pending-border bg-status-pending-bg px-2.5 py-1 text-xs font-semibold text-status-pending-text">
                {request.status}
              </span>
            </div>

            <dl className="grid gap-2 rounded-lg border border-border-subtle bg-surface-overlay p-3 text-xs text-content-secondary sm:grid-cols-2">
              {Object.entries(request.formData).map(([key, value]) => (
                <div key={key} className="min-w-0">
                  <dt className="font-medium text-content-muted">{key}</dt>
                  <dd className="mt-0.5 break-words text-content-primary">{String(value ?? "—")}</dd>
                </div>
              ))}
            </dl>

            <textarea
              value={resolveNotes[request.id] ?? ""}
              onChange={(e) => setResolveNotes((current) => ({ ...current, [request.id]: e.target.value }))}
              placeholder="Resolution notes (optional)"
              rows={2}
              className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
            />

            <div className="flex flex-wrap gap-2 border-t border-border-subtle pt-4">
              <Button
                type="button"
                size="sm"
                disabled={resolvingId === request.id}
                onClick={() => resolveRequest(request.id, "APPROVED")}
              >
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={resolvingId === request.id}
                onClick={() => resolveRequest(request.id, "REJECTED")}
              >
                Reject
              </Button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
